'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { usePathname } from 'next/navigation'
import { useCallback, useContext, useEffect, useRef } from 'react'

import { useAuth } from '@/features/auth/hooks'
import { useAuthGuard } from '@/features/auth/hooks/use-auth-guard'
import { isSharePreviewPath } from '@/features/dashboard/lib/share-layout'
import { getTradePageIdFromPath } from '@/features/dashboard/lib/trade-page-path'
import { WidgetConfigWriteBuffer } from '@/features/dashboard/lib/widget-config-write-buffer'
import { TutorialPreviewContext } from '@/features/tutorial/lib/tutorial-preview-context'
import {
  notifyPendingWidgetConfigChange,
  registerPendingWidgetConfigSource,
} from '@/service/runtime-health/pending-widget-config-registry'
import { uiStateKeys, useUpdateWidget } from '@/service/ui-state.hook'
import { activePageIdAtom } from '@/state/dashboard-layouts'
import type { GetPageLayoutResponse } from '@/types/generated/public_api'

const SYNC_INTERVAL_MS = 500
const widgetConfigWriteBuffer = new WidgetConfigWriteBuffer(
  SYNC_INTERVAL_MS,
  Date.now,
  notifyPendingWidgetConfigChange
)

// The buffer outlives individual widgets and route changes, including writes still in flight.
registerPendingWidgetConfigSource({
  hasPending: () => widgetConfigWriteBuffer.hasPending(),
  flush: () => widgetConfigWriteBuffer.flushPendingOnce(),
})

function getPendingConfigKey(userId: string | null | undefined, pageId: string, widgetId: string) {
  return `${userId ?? ''}\u0000${pageId}\u0000${widgetId}`
}

/**
 * Widget config를 API에 동기화하는 훅.
 * isLoggedIn + activePageId 조건 충족 시에만 API 호출.
 * 변경분은 브라우저 탭 전체에서 공유하는 0.5초 write buffer에서 widget별로 합친다.
 * API는 한 번에 하나만 실행하고, cached config와 같은 값은 전송하지 않는다.
 */
export function useWidgetConfigSync() {
  const activePageId = useAtomValue(activePageIdAtom)
  const pathname = usePathname()
  const routePageId = getTradePageIdFromPath(pathname)
  // `/trade/shared/<id>` is not a page of the viewer's, so getTradePageIdFromPath
  // returns null for it and the fallback would aim every widget's config write at
  // whichever page activePageId still holds. A share preview has no write target.
  const targetPageId = isSharePreviewPath(pathname) ? null : (routePageId ?? activePageId)
  const { isLoggedIn } = useAuthGuard()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { mutateAsync: doUpdateWidgetAsync } = useUpdateWidget()
  // A tutorial's preview card isn't on any page; nothing it does is saved.
  const isTutorialPreview = useContext(TutorialPreviewContext)

  // Handlers below (unmount cleanup, visibilitychange) fire outside the normal render
  // cycle, so they read through refs rather than closing over props/state that can go
  // stale between the effect registering and the handler actually firing.
  const userIdRef = useRef(user?.userId)
  userIdRef.current = user?.userId
  const doUpdateWidgetAsyncRef = useRef(doUpdateWidgetAsync)
  doUpdateWidgetAsyncRef.current = doUpdateWidgetAsync

  const isUnchanged = useCallback(
    (
      pendingUserId: string | null | undefined,
      pageId: string,
      widgetId: string,
      config: Record<string, string>
    ) => {
      const layoutData = queryClient.getQueryData<GetPageLayoutResponse>(
        uiStateKeys.pageLayout(pendingUserId, pageId)
      )
      const currentConfig = layoutData?.widgets.find(
        (widget) => widget.widgetId === widgetId
      )?.config
      return (
        currentConfig !== undefined &&
        Object.entries(config).every(([key, value]) => currentConfig[key] === value)
      )
    },
    [queryClient]
  )

  useEffect(() => {
    // 'hidden' fires on tab switch, backgrounding, and — reliably, unlike
    // beforeunload/unload especially on mobile — right before the tab actually
    // closes. The tab isn't torn down at this point, so an in-flight request here
    // still gets to complete; this is the standard replacement for beforeunload.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') void widgetConfigWriteBuffer.flushOne()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  const syncConfig = useCallback(
    (widgetId: string, config: Record<string, string>) => {
      if (isTutorialPreview || !isLoggedIn || !targetPageId) return

      const pendingKey = getPendingConfigKey(user?.userId, targetPageId, widgetId)
      const pendingUserId = user?.userId
      const pageId = targetPageId
      widgetConfigWriteBuffer.enqueue({
        key: pendingKey,
        config,
        isValid: () => pendingUserId === userIdRef.current,
        isUnchanged: (pendingConfig) => isUnchanged(pendingUserId, pageId, widgetId, pendingConfig),
        write: (pendingConfig) =>
          doUpdateWidgetAsyncRef
            .current({ pageId, widgetId, config: pendingConfig })
            .then(() => {}),
      })
    },
    [isLoggedIn, isTutorialPreview, isUnchanged, targetPageId, user?.userId]
  )

  return { syncConfig }
}
